import { useState, useEffect, useCallback, useRef } from 'react';
import { mqttService, type ServoStatus, type DeviceStatus } from '@/services/mqttService';

interface UseMqttCameraReturn {
  rgb1Angle: number;
  rgb2Angle: number;
  isOnline: boolean;
  connected: boolean;
  connectionError: string;
  sendCommand: (camera: 'rgb1' | 'rgb2', angle: number) => void;
  setRgb1Angle: (v: number) => void;
  setRgb2Angle: (v: number) => void;
}

export function useMqttCamera(deviceId: string): UseMqttCameraReturn {
  const [rgb1Angle, setRgb1Angle] = useState(90);
  const [rgb2Angle, setRgb2Angle] = useState(90);
  const [isOnline, setIsOnline] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const debounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>();

  useEffect(() => {
    if (!deviceId) return;

    let cancelled = false;

    mqttService
      .connect(deviceId)
      .then(() => {
        if (cancelled) return;
        setConnected(true);
        setConnectionError('');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setConnectionError(err.message || 'MQTT 연결 실패');
      });

    const unsubServo = mqttService.onServoStatus((status: ServoStatus) => {
      if (cancelled) return;
      setRgb1Angle(status.rgb1_angle);
      setRgb2Angle(status.rgb2_angle);
      setIsOnline(status.online);
    });

    const unsubDevice = mqttService.onDeviceStatus((status: DeviceStatus) => {
      if (cancelled) return;
      setIsOnline(status.online);
    });

    return () => {
      cancelled = true;
      unsubServo();
      unsubDevice();

      Object.values(debounceRef.current).forEach(clearTimeout);
      debounceRef.current = {};

      mqttService.disconnect();
      setConnected(false);
      setIsOnline(false);
    };
  }, [deviceId]);

  const sendCommand = useCallback(
    (camera: 'rgb1' | 'rgb2', angle: number) => {
      if (!isOnline) return;
      mqttService.sendServoCommand(camera, angle);
    },
    [isOnline],
  );

  const sendDebounced = useCallback(
    (camera: 'rgb1' | 'rgb2', angle: number) => {
      if (debounceRef.current[camera]) {
        clearTimeout(debounceRef.current[camera]);
      }
      debounceRef.current[camera] = setTimeout(() => {
        sendCommand(camera, angle);
      }, 200);
    },
    [sendCommand],
  );

  return {
    rgb1Angle,
    rgb2Angle,
    isOnline,
    connected,
    connectionError,
    sendCommand: sendDebounced,
    setRgb1Angle,
    setRgb2Angle,
  };
}